import type { TFunction } from "i18next"

import { PresentationCancelledError } from "../presentation/presenter"
import {
  RemoteAccessContextChangedError,
  RemoteGraphQLError,
  RemoteHttpError,
  RemoteProtocolError,
  type GraphQLResponseError,
} from "./remote-client"
import { RemoteCommandFailure } from "./remote-command"

/** Translates framework texts, for example `useTranslation("florisoft-framework").t`. */
export type FrameworkTranslate = TFunction<"florisoft-framework">

/**
 * The localized message a user sees for a failed command, a remote error or any other thrown value.
 * A command failure shows its server-authored `displayMessage`, and a string is taken as an already
 * user-facing message. Every other error gets a message for its kind, never its technical text, response
 * body or stack. Returns undefined for a cancellation, which the user caused or which a newer request
 * replaced, so callers show nothing.
 */
export function describeRemoteError(cause: unknown, t: FrameworkTranslate): string | undefined {
  if (isCancellation(cause)) return undefined
  if (typeof cause === "string") return cause.trim() ? cause : t(($) => $.errors.unexpected)
  if (cause instanceof RemoteCommandFailure) {
    return cause.displayMessage?.trim() ? cause.displayMessage : t(($) => $.errors.commandFailed)
  }
  if (cause instanceof RemoteHttpError) return describeHttpStatus(cause.status, t)
  if (cause instanceof RemoteGraphQLError) return describeGraphQLErrors(cause.errors, t)
  if (cause instanceof RemoteProtocolError) return t(($) => $.errors.protocol)
  if (isNetworkError(cause)) return t(($) => $.errors.network)
  return t(($) => $.errors.unexpected)
}

function describeHttpStatus(status: number, t: FrameworkTranslate) {
  if (status === 401) return t(($) => $.errors.unauthorized)
  if (status === 403) return t(($) => $.errors.forbidden)
  if (status === 404) return t(($) => $.errors.notFound)
  if (status === 409) return t(($) => $.errors.conflict)
  if (status >= 500) return t(($) => $.errors.server)
  return t(($) => $.errors.unexpected)
}

/** GraphQL reports authorization in the response body; other GraphQL errors are technical. */
function describeGraphQLErrors(errors: readonly GraphQLResponseError[], t: FrameworkTranslate) {
  const codes = new Set(errors.map((error) => error.extensions?.code))
  if (codes.has("AUTH_NOT_AUTHENTICATED")) return t(($) => $.errors.unauthorized)
  if (codes.has("AUTH_NOT_AUTHORIZED")) return t(($) => $.errors.forbidden)
  return t(($) => $.errors.unexpected)
}

function isCancellation(cause: unknown) {
  return (
    cause instanceof RemoteAccessContextChangedError ||
    cause instanceof PresentationCancelledError ||
    (cause instanceof Error && cause.name === "AbortError") ||
    (typeof DOMException !== "undefined" && cause instanceof DOMException && cause.name === "AbortError")
  )
}

/** fetch rejects with a TypeError when the server cannot be reached; browsers word it differently. */
function isNetworkError(cause: unknown) {
  return cause instanceof TypeError && /fetch|network|load failed/i.test(cause.message)
}
